import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  JournalEntryTable,
  JournalEntryFilters,
  useJournalEntries,
  type JournalEntrySummary,
  type JournalEntryStatus,
} from "@/features/journal-entries";

/**
 * Get the first day of the current month.
 */
function getFirstDayOfMonth(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0] as string;
}

/**
 * Get today's date.
 */
function getToday(): string {
  return new Date().toISOString().split("T")[0] as string;
}

/**
 * Journal entries list page (Diario).
 *
 * Displays a table of journal entries with:
 * - Search by description/reference
 * - Date range filtering
 * - Status filtering
 * - Click to view/edit entry
 */
export function DiarioPage() {
  const navigate = useNavigate();

  // Filter state
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(getFirstDayOfMonth);
  const [endDate, setEndDate] = useState(getToday);
  const [status, setStatus] = useState<JournalEntryStatus | "" | "ALL">("");

  // Debounce search to avoid too many API calls
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useMemo(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch entries with filters
  const { entries, isLoading, error } = useJournalEntries({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    status: status === "ALL" || status === "" ? undefined : status,
    search: debouncedSearch || undefined,
  });

  // Navigate to entry form for editing
  const handleEdit = (entry: JournalEntrySummary) => {
    navigate(`/contabilidad/diario/${entry.id}`);
  };

  // Navigate to entry view (same as edit for now, form will be read-only for POSTED)
  const handleView = (entry: JournalEntrySummary) => {
    navigate(`/contabilidad/diario/${entry.id}`);
  };

  // Navigate to create new entry
  const handleCreate = () => {
    navigate("/contabilidad/diario/nuevo");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2 text-destructive">
        <AlertCircle className="h-8 w-8" />
        <p>Error al cargar los asientos</p>
        <p className="text-sm text-muted-foreground">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Diario General</h1>
          <p className="text-sm text-muted-foreground">
            {entries.length} asientos encontrados
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Asiento
        </Button>
      </div>

      {/* Filters */}
      <JournalEntryFilters
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        status={status}
        onStatusChange={setStatus}
      />

      {/* Entries table */}
      <div className="border rounded-lg p-4 bg-card">
        <JournalEntryTable
          entries={entries}
          onEdit={handleEdit}
          onView={handleView}
        />
      </div>
    </div>
  );
}
